import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { Table } from 'react-bootstrap';

type Props<T extends Record<string, unknown>> = {
  data: T[];
  columns: ColumnDef<T, any>[];
  showFooter?: boolean;
  sorting?: SortingState;
  hiddenColumns?: string[];
};

export default function DataTable<T extends Record<string, unknown>>({
  data,
  columns,
  showFooter = false,
  sorting = [],
  hiddenColumns = [],
}: Props<T>) {
  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
      columnVisibility: hiddenColumns.reduce(
        (acc, columnId) => ({
          ...acc,
          [columnId]: false,
        }),
        {},
      ),
    },
  });

  return (
    <Table striped hover size="sm" responsive>
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map(
              ({ column, colSpan, id, getContext, getSize }) => (
                <th
                  key={id}
                  colSpan={colSpan}
                  style={{
                    width: getSize(),
                  }}
                >
                  {flexRender(column.columnDef.header, getContext())}
                </th>
              ),
            )}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => (
          <tr key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <td key={cell.column.id}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
      {showFooter && (
        <tfoot className="fw-bold bg-light">
          {table.getFooterGroups().map((footerGroup) => (
            <tr key={footerGroup.id}>
              {footerGroup.headers.map(
                ({ column, colSpan, id, getContext, getSize }) => (
                  <td
                    key={id}
                    colSpan={colSpan}
                    style={{
                      width: getSize(),
                    }}
                  >
                    {flexRender(column.columnDef.footer, getContext())}
                  </td>
                ),
              )}
            </tr>
          ))}
        </tfoot>
      )}
    </Table>
  );
}
